import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Building2, Users, BedDouble, LifeBuoy, TrendingUp, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const statusColor = (s) => ({
  founder: 'bg-purple-100 text-purple-800',
  beta: 'bg-emerald-100 text-emerald-800',
  trialing: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-orange-100 text-orange-800',
  canceled: 'bg-gray-100 text-gray-700',
}[s] || 'bg-gray-100 text-gray-700');

const Stat = ({ icon: Icon, label, value }) => (
  <Card className="border-0 card-shadow">
    <CardContent className="p-5 flex items-center gap-4">
      <div className="h-11 w-11 rounded-lg bg-indigo-50 flex items-center justify-center"><Icon className="h-5 w-5 text-indigo-600" /></div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </CardContent>
  </Card>
);

const SuperAdmin = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [o, t] = await Promise.all([
        axios.get(`${API}/platform/overview`),
        axios.get(`${API}/support-tickets`),
      ]);
      setData(o.data);
      setTickets(t.data);
    } catch (e) {
      toast.error('Failed to load platform data');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (user && !user.is_platform_owner) return <Navigate to="/dashboard" />;

  const resolveTicket = async (id) => {
    try { await axios.put(`${API}/support-tickets/${id}/resolve`); toast.success('Ticket resolved'); load(); }
    catch (e) { toast.error('Could not resolve'); }
  };

  return (
    <div className="space-y-8" data-testid="platform-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><TrendingUp className="h-6 w-6 text-indigo-600" /> Platform Overview</h1>
        <p className="text-sm text-gray-600 mt-1">All shelters using ShelterConnectOS — visible only to you.</p>
      </div>

      {loading ? <p className="text-gray-500">Loading...</p> : data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Building2} label="Total shelters" value={data.total_shelters} />
            <Stat icon={CheckCircle} label="Beta slots left" value={data.beta_slots_remaining} />
            <Stat icon={LifeBuoy} label="Open tickets" value={data.open_tickets} />
            <Stat icon={Users} label="Residents (platform)" value={data.total_residents_platform} />
          </div>

          <Card className="border-0 card-shadow">
            <CardHeader><CardTitle className="text-lg">Shelters ({data.total_shelters})</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="shelters-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">Shelter</th><th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Beds</th>
                    <th className="py-2 pr-4">Residents</th><th className="py-2 pr-4">Staff</th>
                    <th className="py-2 pr-4">Onboarded</th><th className="py-2 pr-4">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.shelters.map((s) => (
                    <tr key={s.id} className="border-b last:border-0" data-testid={`shelter-row-${s.id}`}>
                      <td className="py-2 pr-4 font-medium text-gray-900">{s.name}{s.is_founder && <span className="ml-2 text-xs text-purple-600">(founder)</span>}</td>
                      <td className="py-2 pr-4 capitalize">{s.tier}</td>
                      <td className="py-2 pr-4"><Badge className={statusColor(s.subscription_status)}>{s.subscription_status}</Badge></td>
                      <td className="py-2 pr-4">{s.bed_count ?? '—'}</td>
                      <td className="py-2 pr-4">{s.residents_count}</td>
                      <td className="py-2 pr-4">{s.staff_count}</td>
                      <td className="py-2 pr-4">{s.onboarding_completed ? '✓' : '—'}</td>
                      <td className="py-2 pr-4 text-gray-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="border-0 card-shadow">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-amber-600" /> Support tickets</CardTitle></CardHeader>
            <CardContent>
              {tickets.length === 0 ? <p className="text-gray-500 text-sm" data-testid="no-tickets">No tickets reported.</p> : (
                <div className="space-y-3">
                  {tickets.map((t) => (
                    <div key={t.id} className="border rounded-md p-3" data-testid={`ticket-${t.id}`}>
                      <div className="flex justify-between items-start gap-3">
                        <div>
                          <div className="font-medium text-gray-900">{t.subject}</div>
                          <div className="text-xs text-gray-500">{t.shelter_name || 'Unknown shelter'} · {t.submitted_by} · {new Date(t.created_at).toLocaleString()}</div>
                          <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{t.message}</div>
                        </div>
                        {t.status === 'open'
                          ? <Button size="sm" variant="outline" onClick={() => resolveTicket(t.id)} data-testid={`resolve-ticket-${t.id}`}>Resolve</Button>
                          : <Badge className="bg-green-100 text-green-800">resolved</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SuperAdmin;
